export enum CHAIR {
    NONE = -1,
    NORTH = 0,
    EAST = 1,
    SOUTH = 2,
    WEST = 3,
    SPECTATOR = 4,
    VISITOR = 5,
}

export default class Chair {
    index = CHAIR.NONE;

    constructor(chair?: Chair) {
        if (chair) {
            this.index = chair.index;
        }
    }

    static fromInt(i: number) {
        return new Chair().setInt(i);
    }

    static north() {
        return Chair.fromInt(CHAIR.NORTH);
    }

    static east() {
        return Chair.fromInt(CHAIR.EAST);
    }

    static south() {
        return Chair.fromInt(CHAIR.SOUTH);
    }

    static west() {
        return Chair.fromInt(CHAIR.WEST);
    }

    static visitor() {
        return Chair.fromInt(CHAIR.VISITOR);
    }

    static spectator() {
        return Chair.fromInt(CHAIR.SPECTATOR);
    }

    clone() {
        return new Chair(this);
    }

    equals(other?: Chair): boolean {
        return other !== undefined && other.index === this.index;
    }

    getInt(): number {
        return this.index;
    }

    setInt(index: number) {
        this.index = index;
        return this;
    }

    set(chair: Chair) {
        if (chair) {
            this.index = chair.index;
        } else {
            this.index = -1;
        }
        return this;
    }

    setNone() { this.index = CHAIR.NONE; return this; }
    setNorth() { this.index = CHAIR.NORTH; return this; }
    setEast() { this.index = CHAIR.EAST; return this; }
    setSouth() { this.index = CHAIR.SOUTH; return this; }
    setWest() { this.index = CHAIR.WEST; return this; }
    setVisitor() { this.index = CHAIR.VISITOR; return this; }
    setSpectator() { this.index = CHAIR.SPECTATOR; return this; }

    isPlayer(): boolean { return this.index >= CHAIR.NORTH && this.index <= CHAIR.WEST; }
    isSpectator(): boolean { return this.index === CHAIR.SPECTATOR; }
    isVisitor(): boolean { return this.index === CHAIR.VISITOR; }
    isNorth(): boolean { return this.index === CHAIR.NORTH; }
    isSouth(): boolean { return this.index === CHAIR.SOUTH; }
    isWest(): boolean { return this.index === CHAIR.WEST; }
    isEast(): boolean { return this.index === CHAIR.EAST; }
    isNS(): boolean { return this.index === CHAIR.NORTH || this.index === CHAIR.SOUTH; }
    isEW(): boolean { return this.index === CHAIR.EAST || this.index === CHAIR.WEST; }
    isNone(): boolean { return this.index === CHAIR.NONE; }

    partner(): Chair {
        switch (this.index) {
            case CHAIR.NORTH: return new Chair().setInt(CHAIR.SOUTH);
            case CHAIR.EAST: return new Chair().setInt(CHAIR.WEST);
            case CHAIR.SOUTH: return new Chair().setInt(CHAIR.NORTH);
            case CHAIR.WEST: return new Chair().setInt(CHAIR.EAST);
        }
        return new Chair();
    }

    isPartner(c: Chair): boolean {
        switch (this.index) {
            case CHAIR.NORTH: return (c.index === CHAIR.SOUTH);
            case CHAIR.EAST: return (c.index === CHAIR.WEST);
            case CHAIR.SOUTH: return (c.index === CHAIR.NORTH);
            case CHAIR.WEST: return (c.index === CHAIR.EAST);
        }
        return false;
    }

    isPaired(c: Chair): boolean {
        return (this.index === c.index) || this.isPartner(c);
    }

    plus(chair: Chair): Chair {
        let ret = new Chair();

        if (chair.isPlayer() && this.isPlayer()) {
            ret.index = (this.index + chair.index) % 4;
        }
        return ret;
    }

    minus(chair: Chair): Chair {
        let ret = new Chair();

        if (chair.isPlayer() && this.isPlayer()) {
            ret.index = (4 + this.index - chair.index) % 4;
        }
        return ret;
    }

    /**
     * Gets the chair on the next turn.
     * @returns a new instance of a chair of the next turn.
     */
    next(): Chair {
        switch (this.index) {
            case CHAIR.NORTH: return new Chair().setInt(CHAIR.EAST);
            case CHAIR.EAST: return new Chair().setInt(CHAIR.SOUTH);
            case CHAIR.SOUTH: return new Chair().setInt(CHAIR.WEST);
            case CHAIR.WEST: return new Chair().setInt(CHAIR.NORTH);
        }
        return new Chair();
    }

    previous(): Chair {
        switch (this.index) {
            case CHAIR.NORTH: return new Chair().setInt(CHAIR.WEST);
            case CHAIR.EAST: return new Chair().setInt(CHAIR.NORTH);
            case CHAIR.SOUTH: return new Chair().setInt(CHAIR.EAST);
            case CHAIR.WEST: return new Chair().setInt(CHAIR.SOUTH);
        }
        return new Chair();
    }

    setNext(): Chair {
        switch (this.index) {
            case CHAIR.NORTH: this.index = CHAIR.EAST; break;
            case CHAIR.EAST: this.index = CHAIR.SOUTH; break;
            case CHAIR.SOUTH: this.index = CHAIR.WEST; break;
            case CHAIR.WEST: this.index = CHAIR.NORTH; break;
        }

        return this;
    }

    setPrevious(): Chair {
        switch (this.index) {
            case CHAIR.NORTH: this.index = CHAIR.WEST; break;
            case CHAIR.EAST: this.index = CHAIR.NORTH; break;
            case CHAIR.SOUTH: this.index = CHAIR.EAST; break;
            case CHAIR.WEST: this.index = CHAIR.SOUTH; break;
        }

        return this;
    }

    toString(): string {
        return '-NESWVS'[this.index + 1]
    }
}